import { useEffect, useState } from 'react'
import { Container, Paper, Title, PasswordInput, Button, Text, Stack, Alert, Loader, Center, ThemeIcon } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import { XCircle } from 'lucide-react'
import api from '../../api/client'

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'checking' | 'valid' | 'invalid'>('checking')
  const [loading, setLoading] = useState(false)
  const token = searchParams.get('token') || ''

  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }
    api.get(`/auth/check-reset-token?token=${encodeURIComponent(token)}`)
      .then(() => setStatus('valid'))
      .catch(() => setStatus('invalid'))
  }, [token])

  const form = useForm({
    initialValues: { password: '', confirmPassword: '' },
    validate: {
      password: (v) => (v.length >= 6 ? null : 'Password must be at least 6 characters'),
      confirmPassword: (v, values) => (v === values.password ? null : 'Passwords do not match'),
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, password: values.password })
      notifications.show({
        title: 'Password updated',
        message: 'Your password has been reset. Please sign in.',
        color: 'teal',
      })
      navigate('/login')
    } catch (err: any) {
      notifications.show({
        title: 'Reset failed',
        message: err.response?.data?.detail || 'Invalid or expired reset token',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }

  if (status === 'checking') {
    return (
      <Center h={200}>
        <Loader color="grapefruit" />
      </Center>
    )
  }

  if (status === 'invalid') {
    return (
      <Container size={420} my={80}>
        <Paper withBorder shadow="md" p={40} radius="md" bg="var(--sat-bg)">
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="red" variant="light">
              <XCircle size={32} />
            </ThemeIcon>
            <Title order={2} ta="center" style={{ color: 'var(--sat-text)' }}>Invalid Request</Title>
            <Text ta="center" c="dimmed">
              This password reset link is invalid or has already been used. Please request a new one.
            </Text>
            <Button fullWidth variant="outline" color="grapefruit" onClick={() => navigate('/forgot-password')}>
              Request New Link
            </Button>
          </Stack>
        </Paper>
      </Container>
    )
  }

  return (
    <Container size={420} my={80}>
      <Title ta="center" style={{ color: 'var(--sat-text)' }}>Set New Password</Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Enter and confirm your new password below.
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md" bg="var(--sat-bg)">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <PasswordInput
              label="New Password"
              placeholder="At least 6 characters"
              required
              autoComplete="new-password"
              {...form.getInputProps('password')}
            />
            <PasswordInput
              label="Confirm Password"
              placeholder="Repeat your new password"
              required
              autoComplete="new-password"
              {...form.getInputProps('confirmPassword')}
            />
            <Button fullWidth mt="xs" type="submit" color="grapefruit" loading={loading}>
              Update Password
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  )
}
